import React, { useCallback, useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileDown, Loader2, AlertTriangle, CheckCircle2, Save, X } from "lucide-react";
import { toast } from "sonner";

// Prévia do contrato com dados reais; campos pendentes do cliente podem ser corrigidos aqui (salvos no cadastro, com auditoria) antes do PDF.
export const ContractPreviewDialog = ({ contract, onClose, onPdf }) => {
  const [p, setP] = useState(null);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [extra, setExtra] = useState(false);

  const load = useCallback(() => api.get(`/contracts/${contract.id}/preview`).then((r) => { setP(r.data); setEdit({}); }).catch((e) => { toast.error(e.response?.data?.detail || "Erro ao gerar prévia"); onClose(); }), [contract.id, onClose]);
  useEffect(() => { load(); }, [load]);

  const salvar = async () => {
    const vals = Object.fromEntries(Object.entries(edit).filter(([, v]) => v !== ""));
    if (!Object.keys(vals).length) return;
    setSaving(true);
    try {
      const { data: cli } = await api.get(`/clients/${p.cliente_id}`);
      const body = { ...cli, ...vals };
      delete body.id; delete body.created_at; delete body.empresa_id; delete body.is_deleted;
      await api.put(`/clients/${p.cliente_id}`, body);
      toast.success("Cadastro do cliente atualizado — prévia recalculada");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível salvar"); }
    finally { setSaving(false); }
  };
  const pendentes = p?.pendentes || [];
  const campos = extra ? Object.keys(p?.labels || {}) : pendentes.map((x) => x.campo);
  const marca = (t) => t.split("[PENDENTE]").flatMap((s, i, a) => (i < a.length - 1 ? [s, <mark key={i} className="bg-red-100 text-red-700 font-bold px-1 rounded" data-testid="preview-pendente-mark">[PENDENTE]</mark>] : [s]));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="contract-preview-dialog">
        <DialogHeader>
          <DialogTitle>Prévia do contrato — revisar antes de gerar</DialogTitle>
          <DialogDescription>Dados reais do cadastro do cliente, do contrato e da empresa. Nada é inventado: o que falta aparece como <b className="text-red-600">[PENDENTE]</b>.</DialogDescription>
        </DialogHeader>
        {!p ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></div> : (
          <div className="space-y-4">
            {!p.cliente_existe && <p className="text-xs rounded-lg bg-amber-50 border border-amber-200 p-2 text-amber-900" data-testid="preview-sem-cliente">Cliente excluído — usando os dados congelados no contrato. Campos não podem ser corrigidos.</p>}
            <div className={`rounded-lg border p-3 text-sm ${pendentes.length ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="preview-pendentes-box">
              <div className="font-bold flex items-center gap-2">{pendentes.length ? <><AlertTriangle className="w-4 h-4 text-red-600" /> {pendentes.length} campo(s) do cliente pendente(s) para este modelo</> : <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Todos os campos usados pelo modelo estão preenchidos</>}</div>
              {p.cliente_existe && (
                <div className="mt-2 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {campos.map((k) => (
                      <div key={k}><Label className="text-xs">{p.labels[k]}</Label>
                        <Input value={edit[k] ?? p.cliente[k] ?? ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} placeholder={pendentes.some((x) => x.campo === k) ? "pendente" : ""} data-testid={`preview-campo-${k}`} className="h-8 text-sm" /></div>
                    ))}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={salvar} disabled={saving || !Object.keys(edit).length} className="gap-1 bg-onyx text-white" data-testid="preview-salvar-btn">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salvar correções no cadastro</Button>
                    <Button size="sm" variant="ghost" onClick={() => setExtra(!extra)} data-testid="preview-toggle-campos">{extra ? "Mostrar só pendentes" : "Editar outros campos do cliente"}</Button>
                  </div>
                </div>
              )}
            </div>
            <div className="rounded-lg border p-4 bg-white text-sm space-y-3" data-testid="preview-documento">
              <div className="text-center font-extrabold">{p.titulo}</div>
              <div className="text-xs text-slate-600 grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="preview-cabecalho">
                <div><b>Contrato:</b> {p.cabecalho.numero} • {fmtDate(p.cabecalho.data_contrato)}</div>
                <div><b>Cliente:</b> {marca(p.cabecalho.cliente_nome || "[PENDENTE]")}</div>
                <div><b>CPF:</b> {marca(p.cabecalho.cpf || "[PENDENTE]")}</div>
                <div><b>Telefone:</b> {marca(p.cabecalho.cliente_telefone || "[PENDENTE]")}</div>
                <div className="sm:col-span-2"><b>Endereço:</b> {marca(p.cabecalho.cliente_endereco || "[PENDENTE]")}</div>
              </div>
              {p.clausulas.map((c, i) => <div key={c.titulo || i}><div className="font-bold text-xs">{c.titulo}</div><p className="text-xs text-slate-700 whitespace-pre-wrap text-justify">{marca(c.texto)}</p></div>)}
              {contract.status === "contrato_pendente" && contract.exige_assinatura !== false && <p className="text-[11px] text-amber-800 bg-amber-50 rounded p-2">A assinatura do contrato somente será necessária quando solicitada pela empresa.</p>}
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <Button variant="ghost" onClick={onClose} className="gap-1" data-testid="preview-fechar"><X className="w-4 h-4" /> Fechar</Button>
              <Button onClick={onPdf} className="gap-2 bg-gold hover:bg-[hsl(var(--gold-hover))] text-black font-semibold" data-testid="preview-gerar-pdf">
                <FileDown className="w-4 h-4" /> {pendentes.length ? "Gerar PDF mesmo com pendências" : "Gerar PDF final"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
